import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    q: "How does AdBit analyze my ads?",
    a: "Every ad goes through a dual-AI pipeline. First, Google Cloud Vision AI extracts objective data from your image — detecting objects, faces, logos, text, colors, and composition. Then, a neuroscience-trained language model interprets that data through peer-reviewed principles to score attention, cognitive clarity, emotional connection, brand visibility, memorability, and CTA effectiveness.",
  },
  {
    q: "What types of ads can I analyze?",
    a: "You can analyze any static image ad: social media creatives (Instagram, Facebook, TikTok, LinkedIn), display banners, YouTube thumbnails, print ads, out-of-home (OOH) designs, and more. Upload a JPG, PNG, or WebP and get results in under 60 seconds.",
  },
  {
    q: "What science and technology is this based on?",
    a: "Our analysis engine combines two layers. First, peer-reviewed neuroscience principles spanning 30+ years of published research in visual attention, cognitive load, emotional encoding, memory consolidation, and behavioral persuasion. Second, real-time computer vision and machine learning models that extract and interpret visual signals from your ad — detecting elements, composition, color patterns, and text hierarchy. The combination of neuroscience theory with ML-driven visual analysis is what powers our scoring and predictions.",
  },
  {
    q: "How is this different from A/B testing?",
    a: "A/B testing tells you which version won after spending money and time. AdBit predicts which version will perform better before you spend anything. Use our analysis to create stronger variants first, then A/B test the optimized versions for even better results.",
  },
  {
    q: "How reliable are the predictions?",
    a: "Our predictions are based on well-established neuroscience principles and calibrated against real 2025 industry benchmarks across 16+ verticals. While no predictive model is 100% accurate, the neuroscience foundations we use are backed by decades of peer-reviewed research. We're transparent: these are science-based estimates, not guarantees.",
  },
  {
    q: "Is my creative content secure?",
    a: "Yes. All uploads are transmitted over HTTPS and stored in MongoDB Atlas which provides encryption at rest. We do not share, sell, or use your creative content for any purpose other than generating your analysis.",
  },
  {
    q: "What do I get with each analysis?",
    a: "Every analysis includes: a Neuro Score (0-100), 6-dimension breakdown (attention, cognitive clarity, emotional connection, brand visibility, memorability, CTA effectiveness), engagement predictions calibrated to your platform, a memory retention curve, AI-generated recommendations, and audience segmentation — all in under 60 seconds.",
  },
  {
    q: "Can my team collaborate on AdBit?",
    a: "Yes. Agency and Enterprise plans include team dashboards where multiple members can share analyses, organize work into folders by brand or project, and manage access with role-based permissions (owner, admin, editor, viewer).",
  },
  {
    q: "Will you support video ads?",
    a: "Video ad analysis is on our roadmap. We're developing frame-by-frame attention prediction and emotional arc mapping. Sign up to be notified when it launches.",
  },
];

export default function FaqSection() {
  return (
    <section id="faq" className="py-24" data-testid="faq-section">
      <div className="max-w-[1200px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#0F172A' }}>
            Frequently Asked <span className="gradient-text">Questions</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="max-w-2xl mx-auto"
        >
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="bg-white rounded-xl border border-slate-200 px-5 overflow-hidden"
              >
                <AccordionTrigger className="text-left text-sm font-semibold hover:no-underline py-4" style={{ color: '#0F172A' }}>
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed" style={{ color: '#475569' }}>
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
